/// <amd-module name="Dashboards/DashboardView/src/components/DashboardContent" />
import * as React from "react";
import { connect } from "react-redux";

import { Dashboard } from "../model/Dashboard";
import { DashboardViewAwareState } from "../model/DashboardViewState";
import { PresentationMode } from "../model/PresentationMode";
import { Widget } from "../model/Widget";
import { DashboardActions } from "../redux/actions/Actions";
import * as Selectors from "../redux/selectors/Selectors";
import { EmptyDashboard } from "./body/EmptyDashboard";
import { ConnectedVirtualizedGrid } from "./body/VirtualizedGrid";
import { DashboardTemplatePageProxy } from "./complementary/DashboardTemplatePageProxy";
import { InlineConfigFiller } from "./complementary/InlineConfigFiller";
import { WidgetCatalogProxy } from "./complementary/WidgetCatalogProxy";
import { WidgetConfigProxy } from "./complementary/WidgetConfigProxy";
import { ConfigModeHeader } from "./headers/ConfigModeHeader";
import { ConnectedDashboardSettingsManager } from "./headers/DashboardSettingsManager";
import { EditModeHeader } from "./headers/EditModeHeader";
import { ConnectedViewModeHeader } from "./headers/ViewModeHeader";

import { isFeatureEnabled } from "VSS/Platform/Feature";
import { VssComponent } from "VSS/Platform/Layout";

import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import { ThrottlingMessageBar } from "Dashboards/RateLimiter/ThrottlingMessageBar";

import { ITeamData } from "Tfs/Platform/Page";
import { ITfsPageDataAwareState } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataContracts";
import { ConnectedCopyDashboardManager } from "./headers/CopyDashboardManager";

interface DashboardContentProps extends ConnectedDashboardContentOwnProps, ConnectedDashboardContentMappedProps {}

interface ConnectedDashboardContentOwnProps {}

interface ConnectedDashboardContentMappedProps {
    /** Extended Team + Project context object as required by TFS team-scoped REST contract.*/
    team: TeamContext;
    /** Minimal Team Id/Name pair used by Widget/Config Hosts.*/
    widgetTeamContext: ITeamData;
    dashboard: Dashboard;
    widgets: ReadonlyArray<Widget>;
    showDashboardSettingsManager: boolean;
    showCopyDashboardPanel: boolean;
    canEditDashboard: boolean;
    currentPresentationMode: PresentationMode;
    hideHeader: boolean;
    showThrottlingWarning: boolean;
    previewWidgetConfig: Widget | undefined;
    showDashboardTemplatePage: boolean;
}

/**
 * Renders "happy-path" scenario of dashboard content.
 *   - header
 *   - body
 *      - dashboard grid
 *      - empty dashboard
 *   - complementary content
 *      - config
 *      - catalog
 */
class DashboardContent extends VssComponent<DashboardContentProps & typeof Actions> {
    private droppableAreaRef: React.RefObject<HTMLDivElement> = React.createRef();

    private enabledDashboardTemplatePage: boolean =
        this.props.canEditDashboard && isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards-web.dashboard-template-page-feature", false);

    public componentDidMount(): void {
        this.props.onDashboardInitialized();
    }

    public render(): JSX.Element | null {
        // Applying scroll-hidden to these two divs to help IE/Edge correctly apply the scrollbars to our grid
        return (
            <div className="dashboard-content flex-grow flex-row scroll-hidden">
                <div className="primary-content flex-grow flex-column scroll-hidden">
                    {this.renderMessageBars()}
                    {this.renderHeader()}
                    <div ref={this.droppableAreaRef} className="flex-grow flex-column scroll-hidden">
                        <ConnectedVirtualizedGrid
                            key="dashboard-virtualized-grid"
                            renderOnEmpty={this.renderOnEmpty(this.props.showDashboardTemplatePage)}
                        />
                    </div>
                </div>
                {this.renderSpecialContent()}
            </div>
        );
    }

    /** Check that a widget from catalog is dragged into the visible area of the dashboard */
    private isValidDropPoint = (x: number, y: number): boolean => {
        const dashboardBox = this.droppableAreaRef.current!.getBoundingClientRect();

        return dashboardBox.left < x && x < dashboardBox.right && dashboardBox.top < y && y < dashboardBox.top + dashboardBox.height;
    };

    private renderMessageBars(): JSX.Element | null {
        return this.props.showThrottlingWarning ? <ThrottlingMessageBar onDismiss={this.props.onThrottlingDismissed} /> : null;
    }

    /** Conditionally Render the appropriate header based on current state. */
    private renderHeader(): JSX.Element | null {
        let header: JSX.Element | null = null;
        if (!this.props.hideHeader) {
            switch (this.props.currentPresentationMode) {
                case PresentationMode.View:
                case PresentationMode.Copy:
                    header = (
                        <>
                            <ConnectedViewModeHeader onEnterEditClick={this.props.onEditClick} dashboard={this.props.dashboard} pageContext={this.context.pageContext} />
                            {this.props.showDashboardSettingsManager && <ConnectedDashboardSettingsManager />}
                        </>
                    );
                    break;
                case PresentationMode.Edit:
                    header = <EditModeHeader onDoneEditing={this.props.onDoneEditingClick} />;
                    break;
                case PresentationMode.Config:
                    header = <ConfigModeHeader />;
                    break;
            }
        }

        return header;
    }

    /** Used for rendering Panels and other overlay style content outside the normal layout */
    private renderSpecialContent(): JSX.Element | undefined {
        let content = undefined;

        if (this.props.currentPresentationMode === PresentationMode.Edit) {
            content = (
                <WidgetCatalogProxy
                    projectId={this.props.team.projectId}
                    onAdd={this.props.onAddWidgetFromCatalog}
                    isValidDropPoint={this.isValidDropPoint}
                    onDrop={this.props.onDropWidgetFromCatalog}
                />
            );
        } else if (this.props.currentPresentationMode === PresentationMode.Config) {
            content = (
                <>
                    <InlineConfigFiller contributionId={this.props.previewWidgetConfig!.contributionId} />
                    <WidgetConfigProxy
                        config={this.props.previewWidgetConfig!}
                        parentProps={{
                            onDismiss: this.props.onDismissConfiguration,
                            onSave: this.props.onSaveWidgetConfig,
                            onUpdate: this.props.onUpdateWidgetConfig,
                            onTogglePreviewHighlighting: this.props.onTogglePreviewHighlighting
                        }}
                        teamContext={this.props.widgetTeamContext}
                        projectId={this.props.team.projectId}
                    />
                </>
            );
        } else if (this.props.showCopyDashboardPanel) {
            content = (<
                ConnectedCopyDashboardManager
                    teamContext={this.props.team}
                />
            );
        }
        return content;
    }

    private renderOnEmpty = (showDashboardTemplatePage: boolean): (() => JSX.Element | null) => {
        if (showDashboardTemplatePage && this.enabledDashboardTemplatePage) {
            return this.renderDashboardTemplatePage;
        } else {
            return this.renderEmptyDashboard;
        }
    };

    private renderDashboardTemplatePage = (): JSX.Element => {
        return (
            <DashboardTemplatePageProxy
                onEmptyDashboardClick={this.props.onDashboardTemplateClick}
                refreshDashboard={() => {
                    this.props.onDashboardRefreshRequired(false);
                }}
                teamContext={this.props.team}
                dashboard={this.props.dashboard}
            />
        );
    };

    private renderEmptyDashboard = (): JSX.Element => {
        return <EmptyDashboard onAddWidgetClick={this.props.onEditClick} canEditDashboard={this.props.canEditDashboard} />;
    };
}

const Actions = {
    onDashboardInitialized: DashboardActions.notifyDashboardInitialized,
    onThrottlingDismissed: DashboardActions.dismissThrottlingMessage,
    onEditClick: DashboardActions.showEditMode,
    onDoneEditingClick: DashboardActions.hideEditMode,
    onDashboardTemplateClick: DashboardActions.hideDashboardTemplatePage,
    onDashboardRefreshRequired: DashboardActions.refreshDashboard,
    onAddWidgetFromCatalog: DashboardActions.addWidgetFromCatalog,
    onDropWidgetFromCatalog: DashboardActions.dropWidgetFromCatalog,

    onDismissConfiguration: DashboardActions.hideConfigMode,
    onSaveWidgetConfig: DashboardActions.savePreviewWidgetConfig,
    onUpdateWidgetConfig: DashboardActions.updatePreviewWidgetConfig,
    onTogglePreviewHighlighting: DashboardActions.togglePreviewHighlighting,
};

function mapStateToProps(state: DashboardViewAwareState & ITfsPageDataAwareState): ConnectedDashboardContentMappedProps {
    return {
        team: Selectors.getDashboardTeamContext(state),
        dashboard: Selectors.getDashboard(state)!,
        widgets: Selectors.getActiveWidgets(state),
        showDashboardSettingsManager: Selectors.showDashboardSettingsManager(state),
        showCopyDashboardPanel: Selectors.showCopyDashboardPanel(state),
        canEditDashboard: Selectors.canEditDashboard(state),
        currentPresentationMode: Selectors.getPresentationMode(state),
        hideHeader: Selectors.getEmbedded(state),
        showThrottlingWarning: Selectors.showThrottlingWarning(state),
        previewWidgetConfig: Selectors.getPreviewWidgetConfig(state),
        showDashboardTemplatePage: Selectors.showDashboardTemplatePage(state),
        widgetTeamContext: Selectors.getTeamDataOrDefault(state)
    };
}

export const ConnectedDashboardContent = connect(
    mapStateToProps,
    Actions
)(DashboardContent);
